import React, { useState, useEffect, useCallback, useRef } from 'react';
import { predefinedSocialTypes, getIconForType } from '../../utils/socialLinks/socialLinkIcons';
import { Toast } from '../common';
import { ToolbarButton } from '../common/toolbar';

// 커스텀 훅 - 저장 관리
const useSaveManager = (onSave) => {
  const [saveStatus, setSaveStatus] = useState('saved');
  const [lastSavedTime, setLastSavedTime] = useState(new Date());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [toastConfig, setToastConfig] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToastConfig({ message, type });
  }, []);

  const performSave = useCallback(async (message, type = 'success') => {
    setSaveStatus('saving');
    try {
      await onSave();
      setSaveStatus('saved');
      setLastSavedTime(new Date());
      setHasUnsavedChanges(false);
      showToast(message, type);
    } catch (error) {
      setSaveStatus('error');
      console.error('저장 실패:', error);
      showToast('저장 실패', 'error');
    }
  }, [onSave, showToast]);

  const handleImmediateSave = useCallback((message = '삭제되었습니다') => {
    const type = message.includes('삭제') ? 'error' : 'success';
    return performSave(message, type);
  }, [performSave]);

  const handleManualSave = useCallback((setHasEverSaved) => {
    if (setHasEverSaved) setHasEverSaved(true);
    return performSave('저장되었습니다');
  }, [performSave]);

  const handleAutoSave = useCallback((setHasEverSaved) => {
    if (setHasEverSaved) setHasEverSaved(true);
    return performSave('자동 저장되었습니다');
  }, [performSave]);

  return {
    saveStatus,
    lastSavedTime,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    setSaveStatus,
    toastConfig,
    setToastConfig,
    handleImmediateSave,
    handleManualSave,
    handleAutoSave,
  };
};

// 공통 컴포넌트들
const SaveStatusDisplay = ({ saveStatus, lastSavedTime, hasUnsavedChanges, onManualSave }) => {
  const getStatusText = () => {
    switch (saveStatus) {
      case 'saving': return '저장 중...';
      case 'saved': 
        return `마지막 저장: ${lastSavedTime.toLocaleTimeString('ko-KR', { 
          hour: '2-digit', minute: '2-digit', second: '2-digit' 
        })}`;
      case 'unsaved': return '변경사항이 있습니다';
      case 'error': return '저장 실패';
      default: return '';
    }
  };

  const getStatusIcon = () => {
    const icons = {
      saving: (
        <svg className="animate-spin h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ),
      saved: (
        <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      unsaved: (
        <svg className="h-4 w-4 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="2" />
        </svg>
      ),
      error: (
        <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    };

    return icons[saveStatus] || null;
  };

  const statusColorClass = {
    saved: 'text-green-600',
    saving: 'text-blue-600',
    error: 'text-red-600',
    unsaved: 'text-orange-500'
  }[saveStatus] || '';

  return (
    <div className="flex items-center gap-2 text-sm">
      {getStatusIcon()}
      <span className={statusColorClass}>{getStatusText()}</span>
      {saveStatus === 'error' && (
        <button
          onClick={onManualSave}
          className="text-blue-600 hover:text-blue-700 underline text-sm"
        >
          재시도
        </button>
      )}
    </div>
  );
};

const EmptyState = ({ message, subMessage }) => (
  <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
    <p className="text-gray-500 text-sm mb-2">{message}</p>
    <p className="text-gray-400 text-xs">{subMessage}</p>
  </div>
);

const AddButton = ({ onClick, text }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:border-gray-400 hover:text-gray-600"
  >
    {text}
  </button>
);

const DeleteButton = ({ onClick, text = "삭제", small = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-red-600 hover:bg-red-50 rounded-md ${small ? 'text-xs px-2 py-1' : 'px-3 py-1 text-sm'}`}
  >
    {text}
  </button>
);

// 공통 유틸리티 함수들
const hasContent = (item, fields) => {
  // 문자열 배열인 경우 (skills 등)
  if (typeof item === 'string') {
    return item.trim() !== '';
  }
  
  // 객체인 경우
  return fields.some(field => {
    const value = item[field];
    return value && (typeof value === 'string' ? value.trim() : value.length > 0);
  });
};

const createAddHandler = async (items, hasUnsavedChanges, handleAutoSave, onUpdate, newItem, fieldName) => {
  let hasExistingContent = false;
  
  // fieldName에 따라 다르게 체크
  if (fieldName === 'skills' || fieldName === 'keyStrengths') {
    // 문자열 배열의 경우
    hasExistingContent = items.some(item => item && item.trim());
  } else if (fieldName === 'social_links') {
    // 소셜 링크의 경우
    hasExistingContent = items.some(link => 
      (link.url && link.url !== 'https://') || 
      (link.type === 'custom' && link.label && link.label.trim())
    );
  } else {
    // 일반 객체 배열의 경우
    const fieldsToCheck = Object.keys(newItem).filter(key => key !== 'projects');
    hasExistingContent = items.some(item => hasContent(item, fieldsToCheck));
  }
  
  if (hasExistingContent && hasUnsavedChanges) {
    await handleAutoSave();
  }
  
  onUpdate(fieldName, [...items, newItem]);
};

const createRemoveHandler = async (items, index, fields, onUpdate, onInputChange, handleImmediateSave, fieldName, deleteMessage) => {
  const item = items[index];
  const updatedItems = items.filter((_, i) => i !== index);
  const isEmpty = !hasContent(item, fields);
  
  if (isEmpty) {
    onUpdate(fieldName, updatedItems);
  } else {
    onInputChange(fieldName, updatedItems);
    await handleImmediateSave(deleteMessage);
  }
};

// 메인 컴포넌트
const ResumeEditForm = ({ resumeData, onUpdate, onSave, onCancel, isMobile = false }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [hasEverSaved, setHasEverSaved] = useState(false); // 저장 행위 추적
  
  const {
    saveStatus,
    lastSavedTime,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    setSaveStatus,
    toastConfig,
    setToastConfig,
    handleImmediateSave,
    handleManualSave,
    handleAutoSave,
  } = useSaveManager(onSave);

  // 모바일에서 자동 저장을 위한 디바운스 타이머
  const autoSaveTimerRef = useRef(null);

  // 모바일 자동 저장 처리
  useEffect(() => {
    if (isMobile && hasUnsavedChanges) {
      // 기존 타이머가 있으면 취소
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      
      // 1.5초 후 자동 저장
      autoSaveTimerRef.current = setTimeout(() => {
        setHasEverSaved(true);
        handleAutoSave();
      }, 1500);
    }
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, isMobile, handleAutoSave]);

  const tabs = [
    { id: 'basic', name: '기본 정보' },
    { id: 'summary', name: '소개' },
    { id: 'experience', name: '경력 사항' },
    { id: 'education', name: '학력' },
    { id: 'skills', name: '기술' },
    { id: 'certificates', name: '자격증 및 수상 내역' },
    { id: 'papers', name: '논문' }
  ];

  const handleInputChange = useCallback((field, value) => {
    onUpdate(field, value);
    setHasUnsavedChanges(true);
    setSaveStatus('unsaved');
  }, [onUpdate, setHasUnsavedChanges, setSaveStatus]);

  const handleFinishEditing = async () => {
    // 모바일에서는 항상 저장하고 나가기
    if (isMobile) {
      if (hasUnsavedChanges) {
        setSaveStatus('saving');
        try {
          await onSave();
          setSaveStatus('saved');
          setLastSavedTime(new Date());
          setHasUnsavedChanges(false);
          showToast('저장되었습니다!', 'success');
        } catch (error) {
          setSaveStatus('error');
          console.error('저장 실패:', error);
          showToast('저장 실패', 'error');
        }
      }
      onCancel(true); // 모바일에서는 항상 true(저장함)로 전달
      return;
    }
    
    // 데스크탑 로직
    if (hasUnsavedChanges) {
      const userChoice = window.confirm(
        '저장하지 않은 변경사항이 있습니다.\n\n' +
        '확인: 저장하고 나가기\n' +
        '취소: 저장하지 않고 나가기'
      );
      if (userChoice) {
        setHasEverSaved(true);
        await handleManualSave();
        onCancel(true); // 저장했음을 알림
      } else {
        onCancel(false); // 저장하지 않았음을 알림
      }
    } else {
      // 변경사항이 없거나 이미 저장된 경우
      onCancel(hasEverSaved); // hasEverSaved 상태를 전달
    }
  };

  // 브라우저 떠나기 방지
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '저장하지 않은 변경사항이 있습니다. 페이지를 나가면 변경사항이 사라집니다.';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // 키보드 단축키 (Ctrl+S or Cmd+S) - 데스크탑에서만
  useEffect(() => {
    if (isMobile) return; // 모바일에서는 비활성화
    
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasUnsavedChanges) {
          setHasEverSaved(true);
          handleManualSave();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasUnsavedChanges, handleManualSave, isMobile]);

  const tabProps = {
    resumeData,
    onUpdate,
    onInputChange: handleInputChange,
    handleImmediateSave,
    handleAutoSave: () => {
      setHasEverSaved(true);
      return handleAutoSave();
    },
    hasUnsavedChanges
  };

  const tabComponents = {
    basic: <BasicInfoTab {...tabProps} />,
    summary: <SummaryTab {...tabProps} />,
    skills: <SkillsTab {...tabProps} />,
    experience: <ExperienceTab {...tabProps} />,
    education: <EducationTab {...tabProps} />,
    certificates: <CertificatesTab {...tabProps} />,
    papers: <PapersTab {...tabProps} />
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${isMobile ? 'mobile-resume-edit-form' : ''}`} style={{ height: isMobile ? 'auto' : 'calc(100vh - 150px)' }}>
      {/* 헤더 */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">이력서 편집</h2>
            {!isMobile && <p className="text-sm text-gray-600">아래 탭들을 클릭하여 각 섹션을 편집하세요</p>}
          </div>
          
          <div className="flex items-center gap-4">
            {/* Toast 알림 */}
            {toastConfig && (
              <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
                <Toast 
                  message={toastConfig.message}
                  type={toastConfig.type}
                  onClose={() => setToastConfig(null)}
                />
              </div>
            )}
            
            {!toastConfig && (
              <SaveStatusDisplay 
                saveStatus={saveStatus}
                lastSavedTime={lastSavedTime}
                hasUnsavedChanges={hasUnsavedChanges}
                onManualSave={handleManualSave}
              />
            )}

            {/* 데스크탑에서만 저장/닫기 버튼 표시 */}
            {!isMobile && (
              <>
                <ToolbarButton
                  onClick={() => {
                    setHasEverSaved(true);
                    handleManualSave();
                  }}
                  disabled={!hasUnsavedChanges || saveStatus === 'saving'}
                  variant="default"
                  title="Ctrl+S / Cmd+S"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V2" />
                  </svg>
                  저장
                </ToolbarButton>

                <ToolbarButton
                  onClick={handleFinishEditing}
                  variant="default"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d={hasEverSaved ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
                  </svg>
                  {hasEverSaved ? '완료' : '닫기'}
                </ToolbarButton>
              </>
            )}
          </div>
        </div>

        <div className="mt-2 text-xs">
          {isMobile ? (
            // 모바일 안내 메시지
            saveStatus === 'saving' ? (
              <span className="text-blue-600 font-medium">📡 자동 저장 중...</span>
            ) : (
              <span className="text-gray-500">📱 입력 후 1.5초 뒤에 자동으로 저장됩니다.</span>
            )
          ) : (
            // 데스크탑 안내 메시지
            hasUnsavedChanges ? (
              <span className="text-orange-600 font-medium">⚠️ 저장하지 않은 변경사항이 있습니다. 저장 버튼을 눌러주세요!</span>
            ) : (
              <span className="text-gray-500">💡 단축키: Ctrl+S (Mac: Cmd+S)로 빠르게 저장할 수 있습니다.</span>
            )
          )}
        </div>
      </div>
      
      {/* 탭 네비게이션 */}
      <div className={`border-b border-gray-200 ${isMobile ? 'overflow-x-auto' : ''}`}>
        <nav className={`flex ${isMobile ? '' : 'space-x-8'} px-6`} data-tour="edit-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${isMobile ? 'min-w-[80px] mx-2' : ''} ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>
      
      {/* 탭 컨텐츠 */}
      <div className={`p-6 overflow-y-auto ${isMobile ? 'pb-20' : ''}`} style={{ height: isMobile ? 'auto' : 'calc(100vh - 290px)' }}>
        {tabComponents[activeTab]}
      </div>
    </div>
  );
};

// 기본 정보 탭
const BasicInfoTab = ({ resumeData, onInputChange, onUpdate, handleImmediateSave, handleAutoSave, hasUnsavedChanges }) => {
  const getAvailableTypes = () => {
    const usedTypes = (resumeData.social_links || []).map(link => link.type);
    return predefinedSocialTypes.filter(type => 
      !usedTypes.includes(type.type) || type.type === 'custom'
    );
  };

  const addSocialLink = async () => {
    await createAddHandler(
      resumeData.social_links || [],
      hasUnsavedChanges,
      handleAutoSave,
      onUpdate,
      { type: 'custom', label: '', url: '', icon: 'default' },
      'social_links'
    );
  };

  const handleSocialLinkChange = (index, field, value) => {
    const updatedLinks = (resumeData.social_links || []).map((link, i) => {
      if (i === index) {
        const updatedLink = { ...link, [field]: value };
        
        if (field === 'type' && value !== 'custom') {
          const predefined = predefinedSocialTypes.find(t => t.type === value);
          if (predefined) {
            updatedLink.label = predefined.label;
            updatedLink.icon = value;
          }
        }
        
        return updatedLink;
      }
      return link;
    });
    
    const hasLinkContent = updatedLinks.some(link => 
      (link.url && link.url !== 'https://') || 
      (link.type === 'custom' && link.label && link.label.trim())
    );
    
    if (hasLinkContent || field === 'type') {
      onInputChange('social_links', updatedLinks);
    } else {
      onUpdate('social_links', updatedLinks);
    }
  };

  const removeSocialLink = async (index) => {
    await createRemoveHandler(
      resumeData.social_links || [],
      index,
      ['url', 'label'],
      onUpdate,
      onInputChange,
      handleImmediateSave,
      'social_links',
      '소셜 링크가 삭제되었습니다'
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-3">필수 정보</h4>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">이름 (한글) *</label>
              <input
                type="text"
                value={resumeData.name || ''}
                onChange={(e) => onInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="홍길동"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">이름 (영어)</label>
              <input
                type="text"
                value={resumeData.name_en || ''}
                onChange={(e) => onInputChange('name_en', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Gildong Hong"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">이메일 *</label>
            <input
              type="email"
              value={resumeData.email || ''}
              onChange={(e) => onInputChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">직책/타이틀</label>
            <input
              type="text"
              value={resumeData.title || ''}
              onChange={(e) => onInputChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="예: 시니어 프론트엔드 개발자, AI PM"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">전화번호 *</label>
            <input
              type="tel"
              value={resumeData.phone || ''}
              onChange={(e) => onInputChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">소셜 링크 & 추가 정보</h4>
        
        <div className="space-y-3">
          {(resumeData.social_links || []).map((link, index) => (
            <SocialLinkField
              key={index}
              link={link}
              index={index}
              availableTypes={getAvailableTypes()}
              onChange={handleSocialLinkChange}
              onRemove={() => removeSocialLink(index)}
            />
          ))}
          
          {(!resumeData.social_links || resumeData.social_links.length === 0) && (
            <EmptyState 
              message="등록된 링크가 없습니다"
              subMessage='아래의 "+ 링크 추가" 버튼을 눌러 GitHub, LinkedIn 등을 추가해보세요'
            />
          )}
        </div>
        
        <AddButton onClick={addSocialLink} text="+ 링크 추가" />
      </div>
    </div>
  );
};

// 소셜 링크 필드 컴포넌트
const SocialLinkField = ({ link, index, availableTypes, onChange, onRemove }) => {
  const isCustom = link.type === 'custom';
  
  return (
    <div className="flex gap-2 items-start bg-gray-50 p-3 rounded-md">
      <div className="flex-shrink-0 mt-2">
        {getIconForType(link.icon || link.type)}
      </div>
      
      <div className="flex-1 space-y-2">
        <div className="flex gap-2">
          <select
            value={link.type}
            onChange={(e) => onChange(index, 'type', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="custom">사용자 정의</option>
            {availableTypes.map(type => (
              <option key={type.type} value={type.type}>
                {type.label}
              </option>
            ))}
            {link.type !== 'custom' && !availableTypes.find(t => t.type === link.type) && (
              <option value={link.type}>{link.label}</option>
            )}
          </select>
          
          {isCustom && (
            <input
              type="text"
              value={link.label || ''}
              onChange={(e) => onChange(index, 'label', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="항목명 (예: 포트폴리오, 블로그)"
            />
          )}
        </div>
        
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
            https://
          </span>
          <input
            type="text"
            value={link.url ? link.url.replace(/^https?:\/\//i, '') : ''}
            onChange={(e) => {
              const urlWithoutProtocol = e.target.value;
              const fullUrl = urlWithoutProtocol ? `https://${urlWithoutProtocol}` : '';
              onChange(index, 'url', fullUrl);
            }}
            className="w-full pl-16 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={
              predefinedSocialTypes.find(t => t.type === link.type)?.placeholder?.replace(/^https?:\/\//i, '') || 
              'example.com/profile'
            }
          />
        </div>
      </div>
      
      <DeleteButton onClick={onRemove} />
    </div>
  );
};

// 소개 탭
const SummaryTab = ({ resumeData, onUpdate, onInputChange, handleImmediateSave, handleAutoSave, hasUnsavedChanges }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  // 모바일 기기 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobileDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const addKeyStrength = async () => {
    await createAddHandler(
      resumeData.keyStrengths || [],
      hasUnsavedChanges,
      handleAutoSave,
      onUpdate,
      '',
      'keyStrengths'
    );
  };

  const removeKeyStrength = async (index) => {
    const strength = resumeData.keyStrengths[index];
    const newStrengths = (resumeData.keyStrengths || []).filter((_, i) => i !== index);
    
    if (strength && strength.trim()) {
      onInputChange('keyStrengths', newStrengths);
      await handleImmediateSave('핵심 역량이 삭제되었습니다');
    } else {
      onUpdate('keyStrengths', newStrengths);
    }
  };

  // 순서 변경 함수
  const reorderKeyStrengths = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    const newStrengths = [...(resumeData.keyStrengths || [])];
    const [movedItem] = newStrengths.splice(fromIndex, 1);
    newStrengths.splice(toIndex, 0, movedItem);
    
    onInputChange('keyStrengths', newStrengths);
  };

  // 드래그 시작
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // 기본 드래그 이미지 사용 (커스텀 이미지 제거)
  };

  // 드래그 종료
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // 드래그 오버
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex === null) return;
    
    // 같은 위치면 아무것도 하지 않음
    if (draggedIndex === index) {
      setDragOverIndex(null);
      return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;
    
    // 새로운 드롭 위치 계산
    let newDropIndex;
    if (y < height / 2) {
      newDropIndex = index;
    } else {
      newDropIndex = index + 1;
    }
    
    // 드래그 중인 아이템의 현재 위치를 고려
    if (draggedIndex < index && newDropIndex === index + 1) {
      newDropIndex = index;
    } else if (draggedIndex > index && newDropIndex === index) {
      newDropIndex = index;
    }
    
    // 상태가 변경될 때만 업데이트
    if (dragOverIndex !== newDropIndex) {
      setDragOverIndex(newDropIndex);
    }
  };

  // 드래그 리브
  const handleDragLeave = (e) => {
    // 자식 요소로 이동하는 경우는 무시
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverIndex(null);
  };

  // 드롭
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || dragOverIndex === null) {
      return;
    }

    const newStrengths = [...(resumeData.keyStrengths || [])];
    const [movedItem] = newStrengths.splice(draggedIndex, 1);
    
    // dragOverIndex가 실제 삽입 위치를 나타냄
    let insertIndex = dragOverIndex;
    if (draggedIndex < dragOverIndex) {
      insertIndex = dragOverIndex - 1;
    }
    
    newStrengths.splice(insertIndex, 0, movedItem);
    onInputChange('keyStrengths', newStrengths);
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // 터치 이벤트 핸들러 (모바일)
  const handleTouchStart = (e, index) => {
    setTouchStartY(e.touches[0].clientY);
    setDraggedIndex(index);
  };

  const handleTouchMove = (e, index) => {
    if (touchStartY === null) return;
    
    const touchY = e.touches[0].clientY;
    const deltaY = touchY - touchStartY;
    
    // 충분한 움직임이 있을 때만 드래그로 인식
    if (Math.abs(deltaY) > 30) {
      e.preventDefault(); // 스크롤 방지
      
      // 위 또는 아래 아이템과의 위치 교환 판단
      const itemHeight = e.currentTarget.offsetHeight;
      const moveThreshold = itemHeight * 0.5;
      
      if (deltaY < -moveThreshold && index > 0) {
        setDragOverIndex(index - 1);
      } else if (deltaY > moveThreshold && index < (resumeData.keyStrengths || []).length - 1) {
        setDragOverIndex(index + 1);
      }
    }
  };

  const handleTouchEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null) {
      reorderKeyStrengths(draggedIndex, dragOverIndex);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
    setTouchStartY(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">요약</label>
        <textarea
          value={resumeData.summary || ''}
          onChange={(e) => onInputChange('summary', e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="자신의 경력과 전문성을 요약해서 작성하세요"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">핵심 역량</label>
          <span className="text-xs text-gray-500">
            {isMobileDevice ? '길게 누르고 드래그하여 순서 변경' : '드래그하여 순서 변경 가능'}
          </span>
        </div>
        <div className="space-y-2">
          {(resumeData.keyStrengths || []).map((strength, index) => (
            <React.Fragment key={index}>
              {/* 드롭 위치 표시선 (위) */}
              {dragOverIndex === index && draggedIndex !== null && (
                <div className="h-0.5 bg-blue-500 rounded-full mx-2" />
              )}
              
              <div 
                className={`group flex gap-2 transition-all duration-200 ${
                  draggedIndex === index ? 'opacity-50' : ''
                }`}
                draggable={!isMobileDevice}
                onDragStart={(e) => !isMobileDevice && handleDragStart(e, index)}
                onDragEnd={!isMobileDevice ? handleDragEnd : undefined}
                onDragOver={(e) => !isMobileDevice && handleDragOver(e, index)}
                onDragLeave={!isMobileDevice ? handleDragLeave : undefined}
                onDrop={(e) => !isMobileDevice && handleDrop(e, index)}
              onTouchStart={(e) => isMobileDevice && handleTouchStart(e, index)}
              onTouchMove={(e) => isMobileDevice && handleTouchMove(e, index)}
              onTouchEnd={isMobileDevice ? handleTouchEnd : undefined}
            >
              {/* 드래그 핸들 */}
              <div className="flex items-center cursor-move pr-2 text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
              
              <textarea
                value={strength}
                onChange={(e) => {
                  const newStrengths = [...(resumeData.keyStrengths || [])];
                  newStrengths[index] = e.target.value;
                  onInputChange('keyStrengths', newStrengths);
                }}
                rows={2}
                className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none transition-all duration-200 ${
                  draggedIndex === index 
                    ? 'border-gray-300 bg-gray-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                placeholder="예: 백엔드 API 개발: RESTful API 설계 및 구현 전문성"
              />
              <DeleteButton onClick={() => removeKeyStrength(index)} />
            </div>
            
            {/* 드롭 위치 표시선 (마지막 아이템 아래) */}
            {dragOverIndex === (resumeData.keyStrengths || []).length && 
             index === (resumeData.keyStrengths || []).length - 1 && 
             draggedIndex !== null && (
              <div className="h-0.5 bg-blue-500 rounded-full mx-2" />
            )}
            </React.Fragment>
          ))}
          
          {(resumeData.keyStrengths || []).length === 0 && (
            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
              <p className="text-gray-500 text-sm">아직 등록된 핵심 역량이 없습니다</p>
              <p className="text-gray-400 text-xs mt-1">아래 버튼을 눌러 추가해주세요</p>
            </div>
          )}
          
          <AddButton onClick={addKeyStrength} text="+ 핵심 역량 추가" />
        </div>
      </div>
    </div>
  );
};

// 기술 탭
const SkillsTab = ({ resumeData, onUpdate, onInputChange, handleImmediateSave, handleAutoSave, hasUnsavedChanges }) => {
  const [draggedSkillIndex, setDraggedSkillIndex] = useState(null);
  const [dragOverSkillIndex, setDragOverSkillIndex] = useState(null);

  const addSkill = async () => {
    await createAddHandler(
      resumeData.skills || [],
      hasUnsavedChanges,
      handleAutoSave,
      onUpdate,
      '',
      'skills'
    );
  };

  const removeSkill = async (index) => {
    const skill = resumeData.skills[index];
    const newSkills = (resumeData.skills || []).filter((_, i) => i !== index);
    
    if (skill && skill.trim()) {
      onInputChange('skills', newSkills);
      await handleImmediateSave('기술이 삭제되었습니다');
    } else {
      onUpdate('skills', newSkills);
    }
  };

  // 드래그 시작
  const handleSkillDragStart = (e, index) => {
    setDraggedSkillIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // 드래그 종료
  const handleSkillDragEnd = () => {
    setDraggedSkillIndex(null);
    setDragOverSkillIndex(null);
  };

  // 드래그 오버
  const handleSkillDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedSkillIndex === null) return;
    
    // 같은 위치면 아무것도 하지 않음
    if (draggedSkillIndex === index) {
      setDragOverSkillIndex(null);
      return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;
    
    // 새로운 드롭 위치 계산
    let newDropIndex;
    if (y < height / 2) {
      newDropIndex = index;
    } else {
      newDropIndex = index + 1;
    }
    
    // 드래그 중인 아이템의 현재 위치를 고려
    if (draggedSkillIndex < index && newDropIndex === index + 1) {
      newDropIndex = index;
    } else if (draggedSkillIndex > index && newDropIndex === index) {
      newDropIndex = index;
    }
    
    // 상태가 변경될 때만 업데이트
    if (dragOverSkillIndex !== newDropIndex) {
      setDragOverSkillIndex(newDropIndex);
    }
  };

  // 드래그 리브
  const handleSkillDragLeave = (e) => {
    // 자식 요소로 이동하는 경우는 무시
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverSkillIndex(null);
  };

  // 드롭
  const handleSkillDrop = (e, index) => {
    e.preventDefault();
    
    if (draggedSkillIndex === null || dragOverSkillIndex === null) {
      return;
    }

    const newSkills = [...(resumeData.skills || [])];
    const [movedSkill] = newSkills.splice(draggedSkillIndex, 1);
    
    let insertIndex = dragOverSkillIndex;
    if (draggedSkillIndex < dragOverSkillIndex) {
      insertIndex = dragOverSkillIndex - 1;
    }
    
    newSkills.splice(insertIndex, 0, movedSkill);
    onInputChange('skills', newSkills);
    
    setDraggedSkillIndex(null);
    setDragOverSkillIndex(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">기술 스택</label>
          {(resumeData.skills || []).length > 1 && (
            <span className="text-xs text-gray-500">드래그하여 순서 변경 가능</span>
          )}
        </div>
        <div className="space-y-2">
          {(resumeData.skills || []).map((skill, index) => (
            <React.Fragment key={index}>
              {/* 드롭 위치 표시선 (위) */}
              {dragOverSkillIndex === index && draggedSkillIndex !== null && (
                <div className="h-0.5 bg-blue-500 rounded-full mx-2" />
              )}
              
              <div 
                className={`flex gap-2 ${
                  draggedSkillIndex === index ? 'opacity-50' : ''
                }`}
                draggable
                onDragStart={(e) => handleSkillDragStart(e, index)}
                onDragEnd={handleSkillDragEnd}
                onDragOver={(e) => handleSkillDragOver(e, index)}
                onDragLeave={handleSkillDragLeave}
                onDrop={(e) => handleSkillDrop(e, index)}
              >
                {/* 드래그 핸들 */}
                <div className="flex items-center cursor-move pr-2 text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
                
                <input
                  type="text"
                  value={skill}
                  onChange={(e) => {
                    const newSkills = (resumeData.skills || []).map((s, i) => 
                      i === index ? e.target.value : s
                    );
                    onInputChange('skills', newSkills);
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-gray-400 transition-colors"
                  placeholder="예: JavaScript, React, Python, AWS"
                />
                <DeleteButton onClick={() => removeSkill(index)} />
              </div>
              
              {/* 드롭 위치 표시선 (마지막 아이템 아래) */}
              {dragOverSkillIndex === (resumeData.skills || []).length && 
               index === (resumeData.skills || []).length - 1 && 
               draggedSkillIndex !== null && (
                <div className="h-0.5 bg-blue-500 rounded-full mx-2" />
              )}
            </React.Fragment>
          ))}
          
          {(resumeData.skills || []).length === 0 && (
            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
              <p className="text-gray-500 text-sm">아직 등록된 기술이 없습니다</p>
              <p className="text-gray-400 text-xs mt-1">아래 버튼을 눌러 추가해주세요</p>
            </div>
          )}
          
          <AddButton onClick={addSkill} text="+ 기술 추가" />
        </div>
      </div>
    </div>
  );
};

// 경력 탭
const ExperienceTab = ({ resumeData, onUpdate, onInputChange, handleImmediateSave, handleAutoSave, hasUnsavedChanges }) => {
  const updateTitleFromLatestExperience = (experiences) => {
    if (!experiences || experiences.length === 0) return '';
    
    const currentJobs = experiences.filter(exp => 
      exp.endDate && (exp.endDate.trim() === '현재' || exp.endDate.trim().toLowerCase() === 'current')
    );
    
    if (currentJobs.length > 0) {
      const latestCurrentJob = currentJobs.sort((a, b) => 
        (b.startDate || '').localeCompare(a.startDate || '')
      )[0];
      
      if (latestCurrentJob?.position?.trim()) {
        return latestCurrentJob.position.trim();
      }
    }
    
    const pastJobs = experiences.filter(exp => 
      !exp.endDate || (exp.endDate.trim() !== '현재' && exp.endDate.trim().toLowerCase() !== 'current')
    );
    
    if (pastJobs.length > 0) {
      const latestPastJob = pastJobs.sort((a, b) => 
        (b.startDate || '').localeCompare(a.startDate || '')
      )[0];
      
      if (latestPastJob?.position?.trim()) {
        return latestPastJob.position.trim();
      }
    }
    
    return '';
  };

  const handleExperienceChange = (index, field, value) => {
    const updatedExperiences = (resumeData.experiences || []).map((exp, i) => 
      i === index ? { ...exp, [field]: value } : exp
    );
    
    onInputChange('experiences', updatedExperiences);
    
    // 직책-타이틀 자동 연동 기능 제거
    // 사용자가 수동으로 타이틀을 관리하도록 변경
    /*
    if (field === 'position' || field === 'endDate') {
      const newTitle = updateTitleFromLatestExperience(updatedExperiences);
      if (newTitle) {
        onInputChange('title', newTitle);
      }
    }
    */
  };

  const addExperience = async () => {
    await createAddHandler(
      resumeData.experiences || [],
      hasUnsavedChanges,
      handleAutoSave,
      onUpdate,
      {
        company: '',
        position: '',
        startDate: '',
        endDate: '',
        overallDescription: '',
        projects: []
      },
      'experiences'
    );
  };

  const removeExperience = async (index) => {
    const experience = resumeData.experiences[index];
    const updatedExperiences = (resumeData.experiences || []).filter((_, i) => i !== index);
    
    const isEmpty = !hasContent(experience, ['company', 'position', 'startDate', 'endDate', 'overallDescription']) && 
                   (!experience.projects || experience.projects.length === 0);
    
    if (isEmpty) {
      onUpdate('experiences', updatedExperiences);
    } else {
      // const newTitle = updateTitleFromLatestExperience(updatedExperiences);
      
      onInputChange('experiences', updatedExperiences);
      // 직책-타이틀 자동 연동 제거
      /*
      if (newTitle !== undefined) {
        onInputChange('title', newTitle);
      }
      */
      await handleImmediateSave('경력이 삭제되었습니다');
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {(!resumeData.experiences || resumeData.experiences.length === 0) ? (
          <EmptyState 
            message="아직 등록된 경력이 없습니다"
            subMessage='아래의 "+ 경력 추가" 버튼을 눌러 경력을 추가해주세요'
          />
        ) : (
          resumeData.experiences.map((exp, index) => (
            <ExperienceItem
              key={index}
              experience={exp}
              index={index}
              onChange={handleExperienceChange}
              onRemove={() => removeExperience(index)}
              handleImmediateSave={handleImmediateSave}
              resumeData={resumeData}
              onUpdate={onUpdate}
              handleAutoSave={handleAutoSave}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          ))
        )}
      </div>
      <AddButton onClick={addExperience} text="+ 경력 추가" />
    </div>
  );
};

// 경력 아이템 컴포넌트
const ExperienceItem = ({ experience, index, onChange, onRemove, handleImmediateSave, resumeData, onUpdate, handleAutoSave, hasUnsavedChanges }) => {
  const [draggedProjectIndex, setDraggedProjectIndex] = useState(null);
  const [dragOverProjectIndex, setDragOverProjectIndex] = useState(null);

  const handleProjectChange = (projIndex, field, value) => {
    const updatedProjects = (experience.projects || []).map((proj, j) => 
      j === projIndex ? { ...proj, [field]: value } : proj
    );
    onChange(index, 'projects', updatedProjects);
  };

  // 프로젝트 드래그 시작
  const handleProjectDragStart = (e, projIndex) => {
    setDraggedProjectIndex(projIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  // 프로젝트 드래그 종료
  const handleProjectDragEnd = () => {
    setDraggedProjectIndex(null);
    setDragOverProjectIndex(null);
  };

  // 프로젝트 드래그 오버
  const handleProjectDragOver = (e, projIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedProjectIndex === null) return;
    
    // 같은 위치면 아무것도 하지 않음
    if (draggedProjectIndex === projIndex) {
      setDragOverProjectIndex(null);
      return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;
    
    // 새로운 드롭 위치 계산
    let newDropIndex;
    if (y < height / 2) {
      newDropIndex = projIndex;
    } else {
      newDropIndex = projIndex + 1;
    }
    
    // 드래그 중인 아이템의 현재 위치를 고려
    if (draggedProjectIndex < projIndex && newDropIndex === projIndex + 1) {
      newDropIndex = projIndex;
    } else if (draggedProjectIndex > projIndex && newDropIndex === projIndex) {
      newDropIndex = projIndex;
    }
    
    // 상태가 변경될 때만 업데이트
    if (dragOverProjectIndex !== newDropIndex) {
      setDragOverProjectIndex(newDropIndex);
    }
  };

  // 프로젝트 드래그 리브
  const handleProjectDragLeave = (e) => {
    // 자식 요소로 이동하는 경우는 무시
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverProjectIndex(null);
  };

  // 프로젝트 드롭
  const handleProjectDrop = (e, projIndex) => {
    e.preventDefault();
    
    if (draggedProjectIndex === null || dragOverProjectIndex === null) {
      return;
    }

    const updatedProjects = [...(experience.projects || [])];
    const [movedProject] = updatedProjects.splice(draggedProjectIndex, 1);
    
    let insertIndex = dragOverProjectIndex;
    if (draggedProjectIndex < dragOverProjectIndex) {
      insertIndex = dragOverProjectIndex - 1;
    }
    
    updatedProjects.splice(insertIndex, 0, movedProject);
    onChange(index, 'projects', updatedProjects);
    
    setDraggedProjectIndex(null);
    setDragOverProjectIndex(null);
  };

  const addProject = async () => {
    const hasProjectContent = (experience.projects || []).some(proj => 
      hasContent(proj, ['projectName', 'projectDescription'])
    );
    
    const hasExperienceContent = hasContent(experience, ['company', 'position', 'startDate', 'endDate', 'overallDescription']);
    
    if ((hasProjectContent || hasExperienceContent) && hasUnsavedChanges) {
      await handleAutoSave();
    }
    
    const newProject = { projectName: '', projectDescription: '' };
    const updatedProjects = [...(experience.projects || []), newProject];
    const updatedExperience = { ...experience, projects: updatedProjects };
    const allExperiences = (resumeData.experiences || []).map((exp, i) => 
      i === index ? updatedExperience : exp
    );
    onUpdate('experiences', allExperiences);
  };

  const removeProject = async (projIndex) => {
    const project = (experience.projects || [])[projIndex];
    const updatedProjects = (experience.projects || []).filter((_, j) => j !== projIndex);
    
    const isEmpty = !hasContent(project, ['projectName', 'projectDescription']);
    
    if (isEmpty) {
      const updatedExperience = { ...experience, projects: updatedProjects };
      const allExperiences = (resumeData.experiences || []).map((exp, i) => 
        i === index ? updatedExperience : exp
      );
      onUpdate('experiences', allExperiences);
    } else {
      onChange(index, 'projects', updatedProjects);
      await handleImmediateSave('프로젝트가 삭제되었습니다');
    }
  };

  return (
    <div className="border border-gray-200 rounded-md p-4">
      <div className="grid grid-cols-1 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">회사명</label>
          <input
            type="text"
            value={experience.company || ''}
            onChange={(e) => onChange(index, 'company', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">직책</label>
          <input
            type="text"
            value={experience.position || ''}
            onChange={(e) => onChange(index, 'position', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">시작일</label>
            <input
              type="text"
              value={experience.startDate || ''}
              onChange={(e) => onChange(index, 'startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="YYYY.MM"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">종료일</label>
            <input
              type="text"
              value={experience.endDate || ''}
              onChange={(e) => onChange(index, 'endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="YYYY.MM 또는 '현재'"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">경력 상세 내용</label>
          <textarea
            value={experience.overallDescription || ''}
            onChange={(e) => onChange(index, 'overallDescription', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="주요 업무 내용을 설명해주세요. 각 줄의 시작에 '- '나 '• '를 사용하면 불릿 포인트로 표시됩니다."
          />
        </div>
        
        {/* 프로젝트 섹션 */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">주요 프로젝트</label>
              {(experience.projects || []).length > 1 && (
                <span className="text-xs text-gray-500">드래그하여 순서 변경 가능</span>
              )}
            </div>
            <button
              type="button"
              onClick={addProject}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              + 프로젝트 추가
            </button>
          </div>
          
          {(experience.projects || []).length > 0 ? (
            <div className="space-y-3">
              {(experience.projects || []).map((project, projIndex) => (
                <React.Fragment key={projIndex}>
                  {/* 드롭 위치 표시선 (위) */}
                  {dragOverProjectIndex === projIndex && draggedProjectIndex !== null && (
                    <div className="h-0.5 bg-blue-500 rounded-full mx-2" />
                  )}
                  
                  <div 
                    className={`border border-gray-100 rounded-md p-3 bg-gray-50 ${
                      draggedProjectIndex === projIndex ? 'opacity-50' : ''
                    }`}
                    draggable
                    onDragStart={(e) => handleProjectDragStart(e, projIndex)}
                    onDragEnd={handleProjectDragEnd}
                    onDragOver={(e) => handleProjectDragOver(e, projIndex)}
                    onDragLeave={handleProjectDragLeave}
                    onDrop={(e) => handleProjectDrop(e, projIndex)}
                  >
                    <div className="flex gap-2">
                      {/* 드래그 핸들 */}
                      <div className="flex items-start cursor-move pt-1 text-gray-400 hover:text-gray-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                      </div>
                      
                      <div className="flex-1 space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">프로젝트명</label>
                          <input
                            type="text"
                            value={project.projectName || ''}
                            onChange={(e) => handleProjectChange(projIndex, 'projectName', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="예: Scout 프로젝트 (AI 실시간 통역 및 자동 회의록)"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">프로젝트 설명</label>
                          <textarea
                            value={project.projectDescription || ''}
                            onChange={(e) => handleProjectChange(projIndex, 'projectDescription', e.target.value)}
                            rows={4}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                            placeholder="프로젝트 상세 내용을 설명해주세요. 각 줄의 시작에 '- '나 '• '를 사용하면 불릿 포인트로 표시됩니다."
                          />
                        </div>
                        <div className="flex justify-end">
                          <DeleteButton onClick={() => removeProject(projIndex)} text="프로젝트 삭제" small />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* 드롭 위치 표시선 (마지막 프로젝트 아래) */}
                  {dragOverProjectIndex === (experience.projects || []).length && 
                   projIndex === (experience.projects || []).length - 1 && 
                   draggedProjectIndex !== null && (
                    <div className="h-0.5 bg-blue-500 rounded-full mx-2" />
                  )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <EmptyState 
              message="등록된 프로젝트가 없습니다"
              subMessage='위의 "+ 프로젝트 추가" 버튼을 눌러 프로젝트를 추가해주세요'
            />
          )}
        </div>
        
        <div className="flex justify-end pt-2 border-t border-gray-100">
          <DeleteButton onClick={onRemove} text="경력 삭제" />
        </div>
      </div>
    </div>
  );
};

// 학력 탭
const EducationTab = ({ resumeData, onUpdate, onInputChange, handleImmediateSave, handleAutoSave, hasUnsavedChanges }) => {
  const handleEducationChange = (index, field, value) => {
    const updatedEducation = (resumeData.education || []).map((edu, i) => 
      i === index ? { ...edu, [field]: value } : edu
    );
    onInputChange('education', updatedEducation);
  };

  const addEducation = async () => {
    await createAddHandler(
      resumeData.education || [],
      hasUnsavedChanges,
      handleAutoSave,
      onUpdate,
      {
        degree: '',
        school: '',
        major: '',
        startDate: '',
        endDate: ''
      },
      'education'
    );
  };

  const removeEducation = async (index) => {
    await createRemoveHandler(
      resumeData.education || [],
      index,
      ['degree', 'school', 'major', 'startDate', 'endDate'],
      onUpdate,
      onInputChange,
      handleImmediateSave,
      'education',
      '학력이 삭제되었습니다'
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {(!resumeData.education || resumeData.education.length === 0) ? (
          <EmptyState 
            message="아직 등록된 학력이 없습니다"
            subMessage='아래의 "+ 학력 추가" 버튼을 눌러 학력을 추가해주세요'
          />
        ) : (
          resumeData.education.map((edu, index) => (
            <div key={index} className="border border-gray-200 rounded-md p-4">
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">학교명</label>
                  <input
                    type="text"
                    value={edu.school || ''}
                    onChange={(e) => handleEducationChange(index, 'school', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">학위</label>
                  <input
                    type="text"
                    value={edu.degree || ''}
                    onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 학사"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">전공</label>
                  <input
                    type="text"
                    value={edu.major || ''}
                    onChange={(e) => handleEducationChange(index, 'major', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">입학일</label>
                    <input
                      type="text"
                      value={edu.startDate || ''}
                      onChange={(e) => handleEducationChange(index, 'startDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="YYYY.MM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">졸업일</label>
                    <input
                      type="text"
                      value={edu.endDate || ''}
                      onChange={(e) => handleEducationChange(index, 'endDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="YYYY.MM 또는 '재학중'"
                    />
                  </div>
                </div>
                <DeleteButton onClick={() => removeEducation(index)} />
              </div>
            </div>
          ))
        )}
      </div>
      <AddButton onClick={addEducation} text="+ 학력 추가" />
    </div>
  );
};

// 자격증 탭
const CertificatesTab = ({ resumeData, onUpdate, onInputChange, handleImmediateSave, handleAutoSave, hasUnsavedChanges }) => {
  const handleCertificateChange = (index, field, value) => {
    const updatedCertificates = (resumeData.certificates || []).map((cert, i) => 
      i === index ? { ...cert, [field]: value } : cert
    );
    onInputChange('certificates', updatedCertificates);
  };

  const addCertificate = async () => {
    await createAddHandler(
      resumeData.certificates || [],
      hasUnsavedChanges,
      handleAutoSave,
      onUpdate,
      {
        name: '',
        issuer: '',
        date: ''
      },
      'certificates'
    );
  };

  const removeCertificate = async (index) => {
    await createRemoveHandler(
      resumeData.certificates || [],
      index,
      ['name', 'issuer', 'date'],
      onUpdate,
      onInputChange,
      handleImmediateSave,
      'certificates',
      '자격증이 삭제되었습니다'
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {(!resumeData.certificates || resumeData.certificates.length === 0) ? (
          <EmptyState 
            message="아직 등록된 자격증이 없습니다"
            subMessage='아래의 "+ 자격증 추가" 버튼을 눌러 자격증을 추가해주세요'
          />
        ) : (
          resumeData.certificates.map((cert, index) => (
            <div key={index} className="border border-gray-200 rounded-md p-4">
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">자격증명</label>
                  <input
                    type="text"
                    value={cert.name || ''}
                    onChange={(e) => handleCertificateChange(index, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">발급기관</label>
                  <input
                    type="text"
                    value={cert.issuer || ''}
                    onChange={(e) => handleCertificateChange(index, 'issuer', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">취득일</label>
                  <input
                    type="text"
                    value={cert.date || ''}
                    onChange={(e) => handleCertificateChange(index, 'date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="YYYY.MM"
                  />
                </div>
                <DeleteButton onClick={() => removeCertificate(index)} />
              </div>
            </div>
          ))
        )}
      </div>
      <AddButton onClick={addCertificate} text="+ 자격증 추가" />
    </div>
  );
};

// 논문 탭
const PapersTab = ({ resumeData, onUpdate, onInputChange, handleImmediateSave, handleAutoSave, hasUnsavedChanges }) => {
  const handlePaperChange = (index, field, value) => {
    const updatedPapers = (resumeData.papers || []).map((paper, i) => 
      i === index ? { ...paper, [field]: value } : paper
    );
    onInputChange('papers', updatedPapers);
  };

  const addPaper = async () => {
    await createAddHandler(
      resumeData.papers || [],
      hasUnsavedChanges,
      handleAutoSave,
      onUpdate,
      {
        title: '',
        authors: '',
        journal: '',
        year: '',
        doi: '',
        url: '',
        type: 'journal'
      },
      'papers'
    );
  };

  const removePaper = async (index) => {
    await createRemoveHandler(
      resumeData.papers || [],
      index,
      ['title', 'authors', 'journal', 'year', 'doi', 'url'],
      onUpdate,
      onInputChange,
      handleImmediateSave,
      'papers',
      '논문이 삭제되었습니다'
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-yellow-50 p-4 rounded-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-yellow-800">논문 섹션 안내</h4>
            <p className="text-sm text-yellow-700 mt-1">
              논문 정보는 선택 사항이며, 진행률 계산에는 포함되지 않습니다. 연구 경험이나 학술적 성과가 있으신 분만 작성해주세요.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {(!resumeData.papers || resumeData.papers.length === 0) ? (
          <EmptyState 
            message="등록된 논문이 없습니다"
            subMessage="연구 논문이나 학술 발표가 있으시면 아래 버튼을 눌러 추가해주세요"
          />
        ) : (
          resumeData.papers.map((paper, index) => (
            <div key={index} className="border border-gray-200 rounded-md p-4 bg-gray-50">
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">논문 제목 *</label>
                  <input
                    type="text"
                    value={paper.title || ''}
                    onChange={(e) => handlePaperChange(index, 'title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="논문의 제목을 입력하세요"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">저자 *</label>
                  <input
                    type="text"
                    value={paper.authors || ''}
                    onChange={(e) => handlePaperChange(index, 'authors', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 홍길동, 김철수, 이영희 (쉼표로 구분)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">발표 유형</label>
                    <select
                      value={paper.type || 'journal'}
                      onChange={(e) => handlePaperChange(index, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="journal">학술지 논문</option>
                      <option value="conference">학회 발표</option>
                      <option value="preprint">프리프린트</option>
                      <option value="book">도서/챕터</option>
                      <option value="patent">특허</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">발표 연도 *</label>
                    <input
                      type="text"
                      value={paper.year || ''}
                      onChange={(e) => handlePaperChange(index, 'year', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="YYYY"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {paper.type === 'conference' ? '학회명' : paper.type === 'journal' ? '저널명' : '발표처'}
                  </label>
                  <input
                    type="text"
                    value={paper.journal || ''}
                    onChange={(e) => handlePaperChange(index, 'journal', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={
                      paper.type === 'conference' 
                        ? '예: ICML 2024, NIPS 2023' 
                        : paper.type === 'journal'
                        ? '예: Nature, Science, IEEE Transactions'
                        : '발표된 곳을 입력하세요'
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">DOI (선택)</label>
                    <input
                      type="text"
                      value={paper.doi || ''}
                      onChange={(e) => handlePaperChange(index, 'doi', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="10.1000/xyz123"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL/링크 (선택)</label>
                    <input
                      type="url"
                      value={paper.url || ''}
                      onChange={(e) => handlePaperChange(index, 'url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-2 border-t border-gray-200">
                  <DeleteButton onClick={() => removePaper(index)} text="논문 삭제" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      <AddButton onClick={addPaper} text="+ 논문 추가" />
    </div>
  );
};

export default ResumeEditForm;